import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { getClubContracts, downloadClubContract, type ClubContract } from "../../services/planActivation";

/**
 * The club's own contracts, where the club can reach them.
 *
 * A contract that exists only in an email from six months ago is a contract
 * the club does not have. Each row re-renders the document from the body that
 * was frozen when it was issued, so the download is what was agreed to and not
 * what the template says today.
 *
 * Renders nothing when there are none — an empty "Contracte" card on a club
 * that has never had one is just furniture.
 */
export default function ClubContracts({ clubId }: { clubId: string | null | undefined }) {
  const [contracts, setContracts] = useState<ClubContract[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      setContracts(await getClubContracts(clubId));
    } catch {
      // Non-fatal: the invoices around it are still useful.
      setContracts([]);
    }
    setLoaded(true);
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (c: ClubContract) => {
    if (!clubId) return;
    setBusy(c.id);
    try {
      const blob = await downloadClubContract(clubId, c.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nu am putut descărca documentul.");
    }
    setBusy(null);
  };

  if (!loaded || contracts.length === 0) return null;

  return (
    <div className="p-5 bg-white border shadow-sm border-slate-200 rounded-2xl">
      <h3 className="text-base font-bold text-slate-800">Contracte</h3>
      <p className="mt-0.5 text-sm text-slate-500">Documentele semnate între club și iGROWth.</p>

      <div className="mt-4 divide-y divide-slate-100">
        {contracts.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate text-slate-800">{c.title}</p>
              <p className="text-xs text-slate-500">
                {c.number}
                {c.accepted_at
                  ? ` · acceptat pe ${new Date(c.accepted_at).toLocaleDateString("ro-RO")}`
                  : c.sent_at
                    ? ` · trimis pe ${new Date(c.sent_at).toLocaleDateString("ro-RO")}`
                    : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => download(c)}
              disabled={busy === c.id}
              className="px-3 py-2 text-xs font-bold bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50"
            >
              {busy === c.id ? "..." : "Descarcă PDF"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
